import { resetFeedPersonalizationSeed } from './feedPersonalization';

const STORAGE_KEY = 'od_posts_feed_scroll';
const STORAGE_RETURN_SEARCH = 'od_posts_feed_return_search';
const THREAD_RETURN_SCROLL_KEY = '__od_feed_thread_return__';
const FRESH_AFTER_LOGIN_KEY = 'od_posts_fresh_after_login';
const memoryCache = new Map();
const activeRestores = new Map();

const FEED_VIEWS = new Set(['discussions', 'discussions-create']);
const SCROLL_TOP_EPSILON = 3;
const SCROLL_ALIGN_EPSILON = 8;

function parseSearch(search) {
  if (search instanceof URLSearchParams) return search;
  const raw = String(search || '');
  return new URLSearchParams(raw.startsWith('?') ? raw : raw ? `?${raw}` : '');
}

export function getFeedScrollKey(search) {
  const p = parseSearch(search);
  const view = p.get('view') || 'discussions';
  if (!FEED_VIEWS.has(view)) return null;
  return ['view', 'feed', 'sort', 'tab', 'tag'].map((k) => `${k}=${p.get(k) || ''}`).join('&');
}

function readWindowScrollTop() {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function isScrollablePane(el) {
  if (!(el instanceof HTMLElement)) return false;
  const { overflowY } = window.getComputedStyle(el);
  const allowsScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return allowsScroll && el.scrollHeight > el.clientHeight + 1;
}

function getHomeMainElement() {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.support-open-disc-home-main');
  return el instanceof HTMLElement ? el : null;
}

function getActiveFeedScrollElement() {
  const homeMain = getHomeMainElement();
  if (homeMain && isScrollablePane(homeMain)) return homeMain;

  if (typeof document === 'undefined') return null;

  const candidates = [
    document.querySelector('.posts-page-layout__center'),
    document.querySelector('.posts-page-layout'),
    homeMain,
  ];
  for (const candidate of candidates) {
    if (candidate instanceof HTMLElement && isScrollablePane(candidate)) return candidate;
  }
  return null;
}

/** Feed scrollport: internal pane when scrollable, otherwise window (overflow: visible). */
export function getFeedScrollElement() {
  const el = getActiveFeedScrollElement();
  if (el) return el;
  return getHomeMainElement();
}

function feedScrollHeight() {
  const el = getActiveFeedScrollElement();
  if (el) return el.scrollHeight;
  return Math.max(document.documentElement?.scrollHeight || 0, document.body?.scrollHeight || 0);
}

function normalizeSavedScrollTop(top) {
  if (typeof top !== 'number' || !Number.isFinite(top)) return 0;
  const rounded = Math.round(top);
  return rounded <= SCROLL_TOP_EPSILON ? 0 : rounded;
}

function isScrollAligned(target, current) {
  const normalized = normalizeSavedScrollTop(target);
  if (normalized === 0) return current <= SCROLL_TOP_EPSILON;
  return Math.abs(current - normalized) < SCROLL_ALIGN_EPSILON;
}

function clearSavedFeedScrollForKey(key) {
  if (!key) return;
  memoryCache.delete(key);
  memoryCache.delete(THREAD_RETURN_SCROLL_KEY);
  persistScrollMap();
}

export function readFeedScrollTop() {
  const el = getActiveFeedScrollElement();
  if (el) return el.scrollTop;
  return readWindowScrollTop();
}

const DISCUSSION_PAGE_SCROLL_SELECTORS = [
  '.support-open-disc-home-main',
  '.posts-page-layout__center',
  '.posts-page-layout',
  '.support-main',
  '.support-open-disc-shell-layout',
];

function resetScrollContainers(y, selectors = DISCUSSION_PAGE_SCROLL_SELECTORS) {
  if (typeof document === 'undefined') return;
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement) node.scrollTop = y;
    });
  });
  window.scrollTo({ top: y, left: 0, behavior: 'auto' });
  if (document.documentElement) document.documentElement.scrollTop = y;
  if (document.body) document.body.scrollTop = y;
}

function resetAllFeedScrollContainers(y) {
  resetScrollContainers(y, ['.support-open-disc-home-main', '.posts-page-layout__center']);
}

/** Scroll article/thread permalink views to the top (window + nested shell panes). */
export function scrollDiscussionPageToTop({ followUp = false } = {}) {
  resetScrollContainers(0);
  if (!followUp || typeof window === 'undefined') return;
  requestAnimationFrame(() => resetScrollContainers(0));
  window.setTimeout(() => resetScrollContainers(0), 0);
  window.setTimeout(() => resetScrollContainers(0), 120);
}

export function writeFeedScrollTop(top) {
  const y = Math.max(0, Math.round(top));
  if (y === 0) {
    resetAllFeedScrollContainers(0);
    return;
  }
  const el = getActiveFeedScrollElement();
  if (el) {
    el.scrollTop = y;
    return;
  }
  resetAllFeedScrollContainers(y);
}

export function scrollFeedToTop() {
  writeFeedScrollTop(0);
}

/** Set before redirecting to /posts after sign-in so the feed starts at the top. */
export function markPostsFreshAfterLogin() {
  try {
    sessionStorage.setItem(FRESH_AFTER_LOGIN_KEY, '1');
  } catch {
    /* ignore */
  }
  resetFeedPersonalizationSeed();
}

/** True once after login; clears the flag when read. */
export function consumePostsFreshAfterLogin() {
  try {
    const v = sessionStorage.getItem(FRESH_AFTER_LOGIN_KEY);
    if (v) sessionStorage.removeItem(FRESH_AFTER_LOGIN_KEY);
    return v === '1';
  } catch {
    return false;
  }
}

/** Drop saved feed offsets (e.g. guest scroll) so a new session starts at the top. */
export function clearFeedScrollState() {
  memoryCache.clear();
  activeRestores.clear();
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_RETURN_SEARCH);
  } catch {
    /* ignore */
  }
}

function persistScrollMap() {
  try {
    const obj = Object.fromEntries(memoryCache.entries());
    if (Object.keys(obj).length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function hydrateScrollMap() {
  if (memoryCache.size) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    Object.entries(parsed).forEach(([key, top]) => {
      if (typeof top === 'number' && top > 0) memoryCache.set(key, top);
    });
  } catch {
    /* ignore */
  }
}

export function saveFeedScrollPosition(search, { persist = false } = {}) {
  hydrateScrollMap();
  const key = getFeedScrollKey(search);
  if (!key) return;
  // Story modal is an overlay — feed scroll is frozen; don't overwrite the saved return offset.
  const params = parseSearch(search);
  if (String(params.get('story') || '').trim()) return;
  const top = normalizeSavedScrollTop(readFeedScrollTop());
  if (top <= 0) {
    if (memoryCache.has(key)) {
      memoryCache.delete(key);
      if (persist) persistScrollMap();
    }
    return;
  }
  memoryCache.set(key, top);
  if (persist) persistScrollMap();
}

/** Remember feed URL + scroll when opening a thread (for “All discussions” + scroll restore). */
export function saveFeedScrollForThreadReturn(search, { scrollTop } = {}) {
  hydrateScrollMap();
  const rawSearch = String(search || '');
  try {
    sessionStorage.setItem(STORAGE_RETURN_SEARCH, rawSearch);
  } catch {
    /* ignore */
  }
  const key = getFeedScrollKey(search);
  const top = normalizeSavedScrollTop(scrollTop != null ? scrollTop : readFeedScrollTop());
  if (top <= 0) {
    // Keep a prior save when the scrollport cannot be read (e.g. pane timing).
    if (memoryCache.has(THREAD_RETURN_SCROLL_KEY)) return;
    if (key && memoryCache.has(key)) return;
    clearSavedFeedScrollForKey(key);
    return;
  }
  if (key) memoryCache.set(key, top);
  memoryCache.set(THREAD_RETURN_SCROLL_KEY, top);
  persistScrollMap();
}

export function getSavedFeedReturnSearch() {
  try {
    const raw = sessionStorage.getItem(STORAGE_RETURN_SEARCH);
    return raw || null;
  } catch {
    return null;
  }
}

export function mergeSavedFeedListParams(params) {
  const saved = getSavedFeedReturnSearch();
  if (!saved || !(params instanceof URLSearchParams)) return params;
  const savedParams = parseSearch(saved);
  ['feed', 'sort', 'tab', 'tag'].forEach((k) => {
    const v = savedParams.get(k);
    if (v == null || v === '') params.delete(k);
    else params.set(k, v);
  });
  return params;
}

export function buildDiscussionsListPath(pathname, fallbackSearch = '') {
  const saved = getSavedFeedReturnSearch();
  const p = parseSearch(saved || fallbackSearch);
  p.set('view', 'discussions');
  p.delete('d');
  p.delete('club');
  p.delete('story');
  const qs = p.toString();
  return `/posts${qs ? `?${qs}` : ''}`;
}

function clearFeedReturnContext() {
  memoryCache.delete(THREAD_RETURN_SCROLL_KEY);
  try {
    sessionStorage.removeItem(STORAGE_RETURN_SEARCH);
  } catch {
    /* ignore */
  }
}

export function peekFeedScrollPosition(search) {
  hydrateScrollMap();
  const key = getFeedScrollKey(search);
  if (!key) return null;
  const top = memoryCache.get(key);
  return typeof top === 'number' ? top : null;
}

function consumeFeedScrollPosition(search) {
  const key = getFeedScrollKey(search);
  if (key) memoryCache.delete(key);
  clearFeedReturnContext();
  persistScrollMap();
}

export function isLeavingFeedForThread(prevSearch, nextSearch, { nextPathname = '' } = {}) {
  const prev = parseSearch(prevSearch);
  const next = parseSearch(nextSearch);
  const prevView = prev.get('view') || 'discussions';
  const nextView = next.get('view') || 'discussions';
  if (FEED_VIEWS.has(prevView) && /^\/posts\/(?:story|question|article|announcement|p)\/[^/]+$/i.test(String(nextPathname || ''))) {
    return true;
  }
  return FEED_VIEWS.has(prevView) && nextView === 'discussion' && !!next.get('d');
}

export function isReturningToFeedFromThread(prevSearch, nextSearch, { prevPathname = '' } = {}) {
  const prev = parseSearch(prevSearch);
  const next = parseSearch(nextSearch);
  const prevView = prev.get('view') || 'discussions';
  const nextView = next.get('view') || 'discussions';
  if (/^\/posts\/(?:story|question|article|announcement|p)\/[^/]+$/i.test(String(prevPathname || '')) && FEED_VIEWS.has(nextView)) {
    return true;
  }
  return prevView === 'discussion' && FEED_VIEWS.has(nextView);
}

function isStoryOverlaySearchChange(prevSearch, nextSearch, opening) {
  const prev = parseSearch(prevSearch);
  const next = parseSearch(nextSearch);
  const prevView = prev.get('view') || 'discussions';
  const nextView = next.get('view') || 'discussions';
  if (!FEED_VIEWS.has(prevView) || !FEED_VIEWS.has(nextView)) return false;
  const prevStory = !!String(prev.get('story') || '').trim();
  const nextStory = !!String(next.get('story') || '').trim();
  return opening ? !prevStory && nextStory : prevStory && !nextStory;
}

/** Feed → story modal overlay (`?story=` added). */
export function isOpeningStoryOverlay(prevSearch, nextSearch) {
  return isStoryOverlaySearchChange(prevSearch, nextSearch, true);
}

/** Story modal overlay closed (`?story=` removed). */
export function isClosingStoryOverlay(prevSearch, nextSearch) {
  return isStoryOverlaySearchChange(prevSearch, nextSearch, false);
}

function isThreadOverlaySearchChange(prevSearch, nextSearch, opening) {
  const prev = parseSearch(prevSearch);
  const next = parseSearch(nextSearch);
  const prevView = prev.get('view') || 'discussions';
  const nextView = next.get('view') || 'discussions';
  if (!FEED_VIEWS.has(prevView) || !FEED_VIEWS.has(nextView)) return false;
  const prevThread = !!String(prev.get('thread') || '').trim();
  const nextThread = !!String(next.get('thread') || '').trim();
  return opening ? !prevThread && nextThread : prevThread && !nextThread;
}

/** Feed → thread modal overlay (`?thread=` added). */
export function isOpeningThreadOverlay(prevSearch, nextSearch) {
  return isThreadOverlaySearchChange(prevSearch, nextSearch, true);
}

/** Thread modal overlay closed (`?thread=` removed). */
export function isClosingThreadOverlay(prevSearch, nextSearch) {
  return isThreadOverlaySearchChange(prevSearch, nextSearch, false);
}

/**
 * True when search changed in a way that should reset feed scroll (sort/feed/tab/tag/view).
 * False for story/thread/compose overlays and for leaving/returning to a page-mode thread
 * (those preserve or restore scroll instead of jumping to top).
 */
export function shouldResetFeedScrollOnSearchChange(prevSearch, nextSearch) {
  if (prevSearch === nextSearch) return false;
  if (isLeavingFeedForThread(prevSearch, nextSearch)) return false;
  if (isReturningToFeedFromThread(prevSearch, nextSearch)) return false;

  const prevKey = getFeedScrollKey(prevSearch);
  const nextKey = getFeedScrollKey(nextSearch);
  // Same feed list identity: only overlay params (story/thread/compose) changed.
  if (prevKey && nextKey && prevKey === nextKey) return false;

  return true;
}

/** Retry until feed content is tall enough, then consume the saved offset. */
export function scheduleFeedScrollRestore(search) {
  try {
    if (sessionStorage.getItem(FRESH_AFTER_LOGIN_KEY) === '1') return false;
  } catch {
    /* ignore */
  }
  hydrateScrollMap();
  const key = getFeedScrollKey(search);
  if (!key) return false;
  let top = peekFeedScrollPosition(search);
  if (top == null || top <= 0) {
    hydrateScrollMap();
    const fallback = memoryCache.get(THREAD_RETURN_SCROLL_KEY);
    top = typeof fallback === 'number' ? fallback : null;
  }
  top = normalizeSavedScrollTop(top);
  if (top <= 0) return false;
  if (activeRestores.has(key)) return true;

  activeRestores.set(key, { top, attempts: 0 });

  const tick = () => {
    const state = activeRestores.get(key);
    if (!state) return;

    writeFeedScrollTop(state.top);
    const current = readFeedScrollTop();
    const tallEnough = feedScrollHeight() >= state.top + 40;
    const aligned = isScrollAligned(state.top, current);

    if (aligned && tallEnough) {
      activeRestores.delete(key);
      consumeFeedScrollPosition(search);
      return;
    }

    state.attempts += 1;
    if (state.attempts < 48) {
      requestAnimationFrame(tick);
    } else {
      activeRestores.delete(key);
    }
  };

  requestAnimationFrame(tick);
  // Mobile Safari often applies scroll after layout passes complete.
  [80, 200, 400].forEach((ms) => {
    window.setTimeout(() => {
      if (activeRestores.has(key)) tick();
    }, ms);
  });
  return true;
}
